import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  FormControlLabel,
  Grid as Grid2,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { History, Save, Sparkles } from 'lucide-react';
import {
  type Control,
  Controller,
  type DeepPartial,
  type UseFormSetValue,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { RaTextField } from '@modules/_shared/components/form/RaTextfield';
import {
  BrochureImagePreview,
  type PropertyImageBackgroundColor,
  type PropertyImageCrop,
  type PropertyImageSize,
} from '@modules/_shared/components/image-gallery/BrochureImagePreview';
import { useEditableImagesContext } from '@modules/_shared/components/image-gallery/context/editableImagesContext';
import { useEnhanceImageActions } from '@modules/_shared/components/image-gallery/enhance/enhance-image-actions';
import { EnhanceImagePanel } from '@modules/_shared/components/image-gallery/enhance/EnhanceImagePanel';
import { VersionThumb } from '@modules/_shared/components/image-gallery/enhance/EnhanceVersionThumb';
import {
  buildListingImageVersions,
  isPropertyImageVersionConflict,
} from '@modules/_shared/components/image-gallery/enhance/imageVersionUtils';
import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useSingleFlight } from '@modules/_shared/hooks/useSingleFlight';
import { useWatchMultiple } from '@modules/_shared/utils/reactHookForm';
import type {
  PropertyImage,
  PropertyImageForm as PropertyImageFormType,
} from '@modules/listings/ListingPropertyImages/types';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { useDebouncedHandler } from '@shared/hooks/debounce';
import { useLocale } from '@shared/hooks/locale';

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
    {children}
  </Typography>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      color: 'text.secondary',
      fontWeight: 600,
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

type SwitchFieldName =
  | 'display_on_brochure'
  | 'display_on_listing'
  | 'is_floor_plan';

const SwitchField = ({
  control,
  setValue,
  name,
  label,
  onPersist,
}: {
  control: Control<PropertyImageFormType>;
  setValue: UseFormSetValue<PropertyImageFormType>;
  name: SwitchFieldName;
  label: string;
  onPersist: (checked: boolean) => Promise<boolean>;
}) => {
  const [saving, setSaving] = useState(false);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormControlLabel
          sx={{ m: 0, gap: 0.75, opacity: saving ? 0.5 : 1 }}
          control={
            <Switch
              size="small"
              checked={Boolean(field.value)}
              disabled={saving}
              onChange={async (_, checked) => {
                setValue(name, checked, { shouldDirty: false });
                setSaving(true);
                try {
                  const persisted = await onPersist(checked);
                  if (!persisted) {
                    setValue(name, !checked, { shouldDirty: false });
                  }
                } finally {
                  setSaving(false);
                }
              }}
            />
          }
          label={<Typography variant="body2">{label}</Typography>}
        />
      )}
    />
  );
};

const sectionCardSx = {
  p: 2,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
} as const;

const segmentedSx = {
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    textTransform: 'none',
    px: 1.5,
    py: 0.5,
    fontWeight: 600,
    color: 'text.secondary',
    bgcolor: 'background.paper',
    '&.Mui-selected': {
      bgcolor: 'primary.lighter',
      color: 'primary.main',
      borderColor: 'primary.light',
      '&:hover': { bgcolor: 'primary.lighter' },
    },
  },
} as const;

const SegmentedField = ({
  control,
  name,
  label,
  options,
}: {
  control: Control<PropertyImageFormType>;
  name: 'background_color' | 'crop' | 'size';
  label: string;
  options: { label: string; value: string }[];
}) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <SegmentedToggleButtonGroup
          value={field.value ?? null}
          onChange={field.onChange}
          options={options}
          sx={{ gap: 1, flexWrap: 'wrap', ...segmentedSx }}
        />
      )}
    />
  </Box>
);

type BrochureLayoutOptionsProps = {
  control: Control<PropertyImageFormType>;
  imageUrl: string;
};

/* The landscape options for images are not supported yet. */
/* But already implemented in the backend. */
/* Can be accessed by using fieldPrefix="landscape_" */
const BrochureLayoutOptions = ({
  control,
  imageUrl,
}: BrochureLayoutOptionsProps) => {
  const { t } = useLocale();
  const {
    crop,
    size,
    background_color: bg,
  } = useWatchMultiple<
    PropertyImageFormType,
    'crop' | 'size' | 'background_color'
  >({
    control,
    name: ['crop', 'size', 'background_color'],
  });

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12, sm: 8 }}>
        <Stack spacing={2}>
          <SegmentedField
            control={control}
            name="size"
            label={t('Size')}
            options={[
              { label: t('Full page'), value: 'full_page' },
              { label: t('Half page'), value: 'half_page' },
              { label: t('Quarter page'), value: 'quarter_page' },
            ]}
          />
          <SegmentedField
            control={control}
            name="crop"
            label={t('Crop')}
            options={[
              { label: t('Fit'), value: 'fit' },
              { label: t('Fill'), value: 'fill' },
            ]}
          />
          {crop === 'fit' && (
            <SegmentedField
              control={control}
              name="background_color"
              label={t('Background Color')}
              options={[
                { label: t('White'), value: 'white' },
                { label: t('Black'), value: 'black' },
              ]}
            />
          )}
        </Stack>
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 4 }}>
        <FieldLabel>{t('Preview')}</FieldLabel>
        <BrochureImagePreview
          bg={bg as PropertyImageBackgroundColor | null}
          crop={crop as PropertyImageCrop | null}
          size={size as PropertyImageSize | null}
          orientation="portrait"
          imageUrl={imageUrl}
        />
      </Grid2>
    </Grid2>
  );
};

type PropertyImageFormProps = {
  imageId: string;
  excludeFloorPlanOptions?: boolean;
};

export const PropertyImageForm = ({
  imageId,
  excludeFloorPlanOptions = false,
}: PropertyImageFormProps) => {
  const { t } = useLocale();
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const {
    autoSave,
    images,
    updateImage,
    generateMetadataForImage,
    loadingMetadataIds,
    enhanceImage,
    saveImageVersion,
    activateImageVersion,
  } = useEditableImagesContext<PropertyImage, PropertyImageFormType>();

  const image = images.find(img => img.id === imageId);
  const isGeneratingMetadata = loadingMetadataIds?.has(imageId) ?? false;

  const { categories, subcategories, actions } = useEnhanceImageActions();
  const [isEnhancePanelOpen, setIsEnhancePanelOpen] = useState(false);
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);
  const [enhanceSourceImageId, setEnhanceSourceImageId] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [isActivatingVersion, setIsActivatingVersion] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);
  const canEnhance =
    enhanceImage != null &&
    saveImageVersion != null &&
    activateImageVersion != null;

  const listingVersions = useMemo(
    () =>
      image
        ? buildListingImageVersions(
            { id: image.image.id, url: image.image.url ?? null },
            (image.versions ?? []).map(version => ({
              kind: 'saved',
              id: version.id,
              imageId: version.image_id,
              imageUrl: version.image.url ?? null,
              sourceImageId: version.source_image_id ?? null,
              prompt: version.prompt ?? null,
              createdAt: version.created_at,
            })),
          )
        : [],
    [image],
  );
  const currentVersionId =
    listingVersions.find(version => version.imageId === image?.image.id)?.id ??
    '';
  const selectedVersion =
    listingVersions.find(version => version.id === selectedVersionId) ??
    listingVersions.find(version => version.id === currentVersionId);

  useEffect(() => {
    setIsEnhancePanelOpen(false);
    setIsVersionDialogOpen(false);
    setVersionError(null);
  }, [imageId]);

  useEffect(() => {
    setSelectedVersionId(currentVersionId);
    setEnhanceSourceImageId(image?.image.id ?? '');
  }, [currentVersionId, image?.image.id]);

  const openEnhancePanel = useCallback((sourceImageId: string) => {
    setEnhanceSourceImageId(sourceImageId);
    setIsVersionDialogOpen(false);
    setIsEnhancePanelOpen(true);
  }, []);

  const handleActivateVersion = useCallback(async () => {
    if (
      !image ||
      !selectedVersion ||
      selectedVersion.imageId === image.image.id ||
      !activateImageVersion
    ) {
      return;
    }

    setIsActivatingVersion(true);
    setVersionError(null);
    try {
      await activateImageVersion(
        image.id,
        selectedVersion.imageId,
        image.image.id,
      );
      setIsVersionDialogOpen(false);
    } catch (error) {
      console.error('Error activating image version:', error);
      setVersionError(
        isPropertyImageVersionConflict(error)
          ? t('The photo changed in another session. Please try again.')
          : t('Failed to use this version. Please try again.'),
      );
    } finally {
      setIsActivatingVersion(false);
    }
  }, [activateImageVersion, image, selectedVersion, t]);

  const defaultValues = useMemo<DeepPartial<PropertyImageFormType>>(
    () =>
      image != null
        ? {
            title: image.title ?? '',
            description: image.description ?? '',
            display_on_brochure: image.display_on_brochure ?? false,
            display_on_listing: image.display_on_listing ?? false,
            size: image.size ?? 'DIN A4',
            crop: image.crop ?? 'contain',
            background_color: image.background_color ?? 'white',
            ...(excludeFloorPlanOptions
              ? {}
              : { is_floor_plan: image.is_floor_plan ?? false }),
          }
        : {},
    [image, excludeFloorPlanOptions],
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    watch,
    reset,
    setValue,
  } = useForm<PropertyImageFormType>({
    defaultValues,
  });

  const { isInFlight: isSaveInFlight, run: runSave } = useSingleFlight();
  const prevImageIdRef = useRef(imageId);

  const handleSave = useCallback(
    async (data: PropertyImageFormType) => {
      if (!image) {
        return;
      }

      return runSave(async () => {
        await updateImage(image.id, data);
        reset(data, { keepDirtyValues: true });
      });
    },
    [image, runSave, updateImage, reset],
  );

  const debouncedSave = useDebouncedHandler(300, handleSave);
  const save = autoSave ? debouncedSave : handleSave;

  const formValues = autoSave ? watch() : undefined;

  useEffect(() => {
    if (autoSave && isDirty && image && formValues && !isSaveInFlight()) {
      save(formValues);
    }
  }, [autoSave, formValues, isDirty, save, image, isSaveInFlight]);

  useEffect(() => {
    const isSameImage = prevImageIdRef.current === imageId;
    prevImageIdRef.current = imageId;
    reset(defaultValues, isSameImage ? { keepDirtyValues: true } : undefined);
  }, [defaultValues, imageId, reset]);

  const handleCancel = useCallback(() => {
    if (
      isDirty &&
      !autoSave &&
      !window.confirm(
        t('You have unsaved changes. Are you sure you want to close?'),
      )
    ) {
      return;
    }

    reset(defaultValues);
    navigate('.');
  }, [defaultValues, isDirty, autoSave, reset, t, navigate]);

  if (!image) {
    return null;
  }

  return (
    <>
      <form
        onSubmit={autoSave ? undefined : handleSubmit(handleSave)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Stack spacing={2} sx={{ p: 2, pt: 0 }}>
            {canEnhance && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent:
                    listingVersions.length > 1 ? 'space-between' : 'flex-end',
                }}
              >
                {listingVersions.length > 1 && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<History size={17} />}
                    onClick={() => {
                      setSelectedVersionId(currentVersionId);
                      setVersionError(null);
                      setIsVersionDialogOpen(true);
                    }}
                  >
                    {t('Versions')} ({listingVersions.length})
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Sparkles size={17} />}
                  onClick={() => openEnhancePanel(image.image.id)}
                >
                  {t('Enhance image')}
                </Button>
              </Stack>
            )}

            <Box sx={sectionCardSx}>
              <SectionHeader>{t('Display options')}</SectionHeader>
              <Stack
                direction="row"
                sx={{ flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.5 }}
              >
                <SwitchField
                  control={control}
                  setValue={setValue}
                  name="display_on_brochure"
                  label={t('Display on brochure')}
                  onPersist={checked =>
                    updateImage(imageId, { display_on_brochure: checked })
                  }
                />
                <SwitchField
                  control={control}
                  setValue={setValue}
                  name="display_on_listing"
                  label={t('Display on listing')}
                  onPersist={checked =>
                    updateImage(imageId, { display_on_listing: checked })
                  }
                />
                {!excludeFloorPlanOptions && (
                  <SwitchField
                    control={control}
                    setValue={setValue}
                    name="is_floor_plan"
                    label={t('Is floor plan')}
                    onPersist={checked =>
                      updateImage(imageId, { is_floor_plan: checked })
                    }
                  />
                )}
              </Stack>
            </Box>

            <Box sx={sectionCardSx}>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1.25,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('Title & description')}
                </Typography>
                {generateMetadataForImage != null && (
                  <Button
                    variant="text"
                    size="small"
                    loading={isGeneratingMetadata}
                    loadingPosition="start"
                    startIcon={<Sparkles size={16} />}
                    onClick={() => {
                      track(ANALYTICS_EVENTS.AI_GENERATE_CLICKED, {
                        source: 'image_generate_metadata',
                        image_id: imageId,
                      });
                      generateMetadataForImage(imageId);
                    }}
                  >
                    {t('Generate')}
                  </Button>
                )}
              </Stack>
              <Stack spacing={1.5}>
                <RaTextField
                  name="title"
                  label={t('Title')}
                  errors={errors}
                  register={register}
                />
                <RaTextField
                  name="description"
                  label={t('Description')}
                  errors={errors}
                  register={register}
                  multiline
                  minRows={2}
                />
              </Stack>
            </Box>

            <Box sx={sectionCardSx}>
              <SectionHeader>{t('Brochure layout options')}</SectionHeader>
              <BrochureLayoutOptions
                control={control}
                imageUrl={image.image.url ?? ''}
              />
            </Box>
          </Stack>
        </Box>

        {!autoSave && (
          <Box
            component={Paper}
            elevation={3}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              padding: 1,
              borderRadius: 0,
              position: 'sticky',
              bottom: 0,
            }}
          >
            <Button
              variant="outlined"
              color="neutral"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              {t('Cancel')}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isDirty}
              loading={isSubmitting}
              loadingPosition="start"
              startIcon={<Save size={18} />}
            >
              {t('Save')}
            </LoadingButton>
          </Box>
        )}
      </form>
      {canEnhance && (
        <SlideDialog
          open={isVersionDialogOpen}
          onClose={() => setIsVersionDialogOpen(false)}
          title={t('Versions')}
          loading={isActivatingVersion}
          cancelText={t('Close')}
          customPrimaryActions={
            <>
              <Button
                variant="outlined"
                startIcon={<Sparkles size={18} />}
                disabled={
                  isActivatingVersion || selectedVersion?.imageUrl == null
                }
                onClick={() =>
                  selectedVersion && openEnhancePanel(selectedVersion.imageId)
                }
              >
                {t('Enhance image')}
              </Button>
              {selectedVersion?.imageId !== image.image.id && (
                <LoadingButton
                  variant="contained"
                  loading={isActivatingVersion}
                  onClick={handleActivateVersion}
                >
                  {t('Use this version')}
                </LoadingButton>
              )}
            </>
          }
        >
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                height: { xs: 240, sm: 360 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
              }}
            >
              {selectedVersion?.imageUrl == null ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : (
                <Image
                  src={selectedVersion.imageUrl}
                  options={{ h: 720, f: 'jpg' }}
                  objectFit="contain"
                  css={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                overflowX: 'auto',
                pb: 1,
                '&::-webkit-scrollbar': { height: 6 },
              }}
            >
              {listingVersions.map((version, index) => {
                const isCurrent = version.imageId === image.image.id;
                const isUnavailable = version.imageUrl == null;
                const label = [
                  index === 0 ? t('Original') : `v${index}`,
                  isCurrent ? t('Current') : null,
                ]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <Box key={version.id} sx={{ width: 104, flexShrink: 0 }}>
                    <VersionThumb
                      active={selectedVersion?.id === version.id}
                      disabled={isUnavailable || isActivatingVersion}
                      onClick={() => {
                        setSelectedVersionId(version.id);
                        setVersionError(null);
                      }}
                      label={isUnavailable ? t('Version unavailable') : label}
                    >
                      {isUnavailable ? (
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height="100%"
                        />
                      ) : (
                        <Image
                          src={version.imageUrl ?? ''}
                          options={{ w: 208, h: 156, c: 'fill', f: 'jpg' }}
                          objectFit="cover"
                          css={{ width: '100%', height: '100%' }}
                        />
                      )}
                    </VersionThumb>
                  </Box>
                );
              })}
            </Box>

            {versionError && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {versionError}
              </Typography>
            )}
          </Box>
        </SlideDialog>
      )}
      {canEnhance && (
        <EnhanceImagePanel
          open={isEnhancePanelOpen}
          onClose={() => setIsEnhancePanelOpen(false)}
          image={{ id: image.id, url: image.image.url ?? '' }}
          actions={actions}
          categories={categories}
          subcategories={subcategories}
          onEnhance={enhanceImage}
          listingVersioning={{
            propertyImageId: image.id,
            currentImageId: image.image.id,
            initialVersionImageId: enhanceSourceImageId,
            savedVersions: listingVersions,
            onSaveVersion: saveImageVersion,
            onUseVersion: activateImageVersion,
          }}
        />
      )}
    </>
  );
};
